import { useCallback, useRef } from "react";
import { Square } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Arrêt d'une conversation d'agent en cours.
 *
 * Chaque envoi reçoit un identifiant d'exécution (run_id) transmis au serveur.
 * « Arrêter » demande au serveur de tuer l'agent (Claude ou Codex) et ses commandes,
 * puis coupe le flux. Une simple coupure réseau, elle, n'arrête rien : sur mobile,
 * l'agent doit pouvoir finir quand le téléphone se met en veille.
 *
 *   const arret = useArretAgent(token, "/api/agent/stop");
 *   const { signal, run_id } = arret.demarrer();
 *   fetch(url, { body: JSON.stringify({ ..., run_id }), signal })
 *   catch (e) { if (arret.estArret(e)) … }
 *   <BoutonArreter onClick={arret.arreter} />
 */
export function useArretAgent(token, cheminStop) {
  const controleur = useRef(null);
  const execution = useRef(null);
  const demarrer = useCallback(() => {
    controleur.current = new AbortController();
    execution.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    return { signal: controleur.current.signal, run_id: execution.current };
  }, []);
  const arreter = useCallback(async () => {
    const run_id = execution.current;
    try {
      if (run_id && cheminStop) {
        await fetch(`${API_URL}${cheminStop}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ run_id }),
        });
      }
    } catch { /* le flux est coupé quand même */ }
    controleur.current?.abort();
  }, [token, cheminStop]);
  const estArret = useCallback((e) => e?.name === "AbortError", []);
  return { demarrer, arreter, estArret };
}

export default function BoutonArreter({ onClick, className = "", compact = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Arrêter l'agent"
      aria-label="Arrêter l'agent"
      className={compact
        ? `flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-600 text-white ${className}`
        : `inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 ${className}`}
    >
      <Square className="h-3.5 w-3.5 fill-current" />{!compact && " Arrêter"}
    </button>
  );
}
